import React, { useEffect } from "react";
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import {
  IconDeviceFloppy,
  IconDownload,
  IconEye,
  IconFileDescription,
  IconRefresh,
  IconTag,
} from "@tabler/icons-react";
import { toast } from "react-toastify";

import useAlterationForm from "./useAlterationForm";
import AlterationPanelSection from "./AlterationPanelSection";
import {
  ALTERATION_DOCUMENT_TITLE,
  ALTERATION_HEADER_FIELDS,
  ALTERATION_LABEL_FIELDS,
  ALTERATION_MAX_LENGTH,
  ALTERATION_PANELS,
  hasAnyAlterationValue,
} from "./alterationFormConfig";

/**
 * Popup that collects the alteration details for an RMA and turns them into the
 * filled "ASPETTO DHS Alteration & Return Form" PDF.
 *
 * The PDF itself is produced on the backend by filling the original fillable
 * template, so the generated document is byte-for-byte the agency's form with
 * the entered values dropped into its fields.
 *
 * @param {boolean}  show        Modal visibility.
 * @param {Function} onHide      Close handler.
 * @param {string}   rmaId       Rma.rma_id the form belongs to.
 * @param {boolean}  [isViewMode] Render read-only (no save/generate).
 */
function AlterationFormModal({ show, onHide, rmaId, isViewMode = false }) {
  const {
    values,
    setFieldValue,
    isDirty,
    hasSavedForm,
    loadError,
    isLoadingForm,
    isSaving,
    isGenerating,
    loadForm,
    saveForm,
    generatePdf,
  } = useAlterationForm(rmaId);

  useEffect(() => {
    if (show && rmaId) {
      loadForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, rmaId]);

  const isBusy = isLoadingForm || isSaving || isGenerating;
  const isEmptyForm = !hasAnyAlterationValue(values);

  const handleReload = async () => {
    await loadForm();
    toast.info("Form reloaded from the RMA.");
  };

  const handleSave = async () => {
    try {
      const response = await saveForm();
      toast.success(response?.message || "Alteration form saved.");
    } catch (error) {
      toast.error(error?.message || "Could not save the alteration form.");
    }
  };

  const handlePreview = async () => {
    /*
     * Open the tab on the click itself — opening it after the await would be
     * treated as a popup and blocked.
     */
    const previewWindow = window.open("", "_blank");

    try {
      const { url } = await generatePdf({ save: !isViewMode });

      if (previewWindow && !previewWindow.closed) {
        previewWindow.location.href = url;
      } else {
        window.open(url, "_blank");
      }
    } catch (error) {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
      toast.error(error?.message || "Could not generate the PDF.");
    }
  };

  const handleDownload = async () => {
    try {
      const { url, fileName } = await generatePdf({ save: !isViewMode });

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "Alteration-Return-Form.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`${fileName} downloaded.`);
    } catch (error) {
      toast.error(error?.message || "Could not generate the PDF.");
    }
  };

  const renderTextField = (field) => (
    <Col md={field.md || 6} key={field.key}>
      <Form.Group className="mb-3">
        <Form.Label>{field.label}</Form.Label>
        <Form.Control
          type="text"
          value={values[field.key] || ""}
          disabled={isViewMode || isLoadingForm}
          maxLength={
            field.key === "rma_number"
              ? ALTERATION_MAX_LENGTH.label
              : ALTERATION_MAX_LENGTH.header
          }
          placeholder={field.placeholder}
          onChange={(event) => setFieldValue(field.key, event.target.value)}
        />
      </Form.Group>
    </Col>
  );

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="tittle_deal">
          <span className="text-green">ALTERATION &amp; RETURN </span> FORM
          <div className="fs-6 fw-normal text-muted">
            {ALTERATION_DOCUMENT_TITLE}
            {rmaId ? ` — RMA ${rmaId}` : ""}
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoadingForm && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading form…
          </div>
        )}

        {loadError && (
          <Alert variant="danger" className="mb-3">
            {loadError}
          </Alert>
        )}

        {!isLoadingForm && (
          <>
            <Alert variant="light" className="border mb-3">
              <strong>All fields are optional.</strong> Anything left blank prints
              as an empty line on the form, exactly like the paper version.
              {hasSavedForm && (
                <span className="d-block text-muted small mt-1">
                  A saved copy of this form was loaded for RMA {rmaId}.
                </span>
              )}
            </Alert>

            <Card className="mb-3 card_suggestion">
              <Card.Header className="card_header">
                <IconFileDescription stroke={1} /> User &amp; Agency Details
              </Card.Header>
              <Card.Body>
                <Row>{ALTERATION_HEADER_FIELDS.map(renderTextField)}</Row>
              </Card.Body>
            </Card>

            {ALTERATION_PANELS.map((panel) => (
              <AlterationPanelSection
                key={panel.key}
                panel={panel}
                values={values}
                onChange={setFieldValue}
                disabled={isViewMode}
              />
            ))}

            <Card className="mb-0 card_suggestion">
              <Card.Header className="card_header">
                <IconTag stroke={1} /> Detachable Return Label
              </Card.Header>
              <Card.Body>
                <Row>{ALTERATION_LABEL_FIELDS.map(renderTextField)}</Row>
                <div className="text-muted small">
                  Printed under “RMA#” on the return label. The vendor address
                  below it is fixed by the template.
                </div>
              </Card.Body>
            </Card>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <div>
          <Button
            variant="outline-secondary"
            onClick={handleReload}
            disabled={isBusy}
          >
            <IconRefresh stroke={1} size={18} className="me-1" />
            Reset
          </Button>
          {isDirty && !isViewMode && (
            <span className="text-muted small ms-2">Unsaved changes</span>
          )}
        </div>

        <div className="d-flex gap-2">
          {!isViewMode && (
            <Button variant="secondary" onClick={handleSave} disabled={isBusy}>
              {isSaving ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <IconDeviceFloppy stroke={1} size={18} className="me-1" />
              )}
              Save
            </Button>
          )}

          <Button
            variant="outline-warning"
            onClick={handlePreview}
            disabled={isBusy || isEmptyForm}
          >
            <IconEye stroke={1} size={18} className="me-1" />
            Preview PDF
          </Button>

          <Button
            variant="warning"
            onClick={handleDownload}
            disabled={isBusy || isEmptyForm}
          >
            {isGenerating ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <IconDownload stroke={1} size={18} className="me-1" />
            )}
            Generate PDF
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default AlterationFormModal;
